import { __decorate, __metadata } from "tslib";
import { VuexModule, Module, Mutation, Action, getModule } from "vuex-module-decorators";
import store from "../../store";
import { plainToClass } from "class-transformer";
import { getTenantId } from "@/store/helper";
import service from "@/services/api.service";
import { IClientUpdateDTO } from "../models/clientUpdates/clientUpdate";
import { ActionItemModel, IActionItemDTO } from "../models/clientUpdates/actionItem";
import { IActionItemsSearchDataDTO } from "../models/clientUpdates/actionItemsSearchData";
import { IUpdatesTimelineEntryDTO, UpdatesTimelineEntryModel } from "../models/clientUpdates/updatesTimelineEntry";
const emptyActionItemFilterOptions = () => ({ clients: [], teams: [], assignees: [], creators: [] });
// Local calendar date as yyyy-MM-dd, so the server receives the picked day without a timezone shift.
const toApiDate = (value) => {
    const date = new Date(value);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    return `${date.getFullYear()}-${month}-${day}`;
};
let ClientUpdatesModule = /** @class */ (() => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8;
    let ClientUpdatesModule = class ClientUpdatesModule extends VuexModule {
        constructor() {
            super(...arguments);
            this.timeline = [];
            this.tabCounts = { all: 0, client: 0, development: 0, actionItems: 0 };
            this.eligibleAssignees = [];
            // The selected client's teams for the Updates Team filter
            this.teams = [];
            this.clientOptions = [];
            this.interactionTypes = [];
            this.actionItemGrid = [];
            this.actionItemTotalCount = 0;
            this.actionItemFilterOptions = emptyActionItemFilterOptions();
            this.updateSummary = null;
            this.weeklyStandup = [];
            this.apiNode = "PMOClientUpdates";
            // Action item endpoints (Action Items page, and action items raised from Updates) live on their own controller
            this.actionItemsApiNode = "PMOActionItems";
        }
        // Page master data: client picker options and Interaction Types
        async GetUpdatesPageData() {
            var url = `${getTenantId()}/${this.apiNode}/page-data`;
            let { data } = await service.get(url);
            if (data) {
                this.SetClientOptions(data.clients ?? []);
                this.SetInteractionTypes(data.interactionTypes ?? []);
            }
            return data;
        }
        async SetInteractionTypes(interactionTypes) {
            this.interactionTypes = interactionTypes;
        }
        // Timeline, with the client's eligible action item assignees
        async GetUpdatesTimeline(payload) {
            const params = [];
            if (payload.from) {
                params.push("from=" + encodeURIComponent(toApiDate(payload.from)));
            }
            if (payload.to) {
                params.push("to=" + encodeURIComponent(toApiDate(payload.to)));
            }
            if (payload.teamId) {
                params.push("teamId=" + encodeURIComponent(payload.teamId.toString()));
            }
            var url = `${getTenantId()}/${this.apiNode}/` + payload.clientId + "/timeline" + (params.length ? "?" + params.join("&") : "");
            let { data } = await service.get(url);
            if (data) {
                this.SetTimeline(plainToClass(UpdatesTimelineEntryModel, data.entries ?? []));
                this.SetTabCounts({
                    all: data.allCount ?? 0,
                    client: data.clientCount ?? 0,
                    development: data.developmentCount ?? 0,
                    actionItems: data.actionItemCount ?? 0,
                });
                this.SetEligibleAssignees(data.eligibleAssignees ?? []);
            }
            return data;
        }
        // The selected client's teams for the Team filter - its timeline loads once a team is picked
        async GetUpdatesTeams(clientId) {
            var url = `${getTenantId()}/${this.apiNode}/` + clientId + "/teams";
            let { data } = await service.get(url);
            if (data) {
                this.SetTeams(data.teams ?? []);
            }
            return data;
        }
        // No team picked: nothing is loaded, so nothing is listed or counted
        async ClearTimeline() {
            this.SetTimeline([]);
            this.SetTabCounts({ all: 0, client: 0, development: 0, actionItems: 0 });
        }
        async SetTeams(teams) {
            this.teams = teams;
        }
        async SetTimeline(timeline) {
            this.timeline = timeline;
        }
        async SetTabCounts(tabCounts) {
            this.tabCounts = tabCounts;
        }
        // Weekly Standup - every client (A-Z) with its newest N updates of any type, or those in the range
        async GetWeeklyStandup(payload) {
            var query = payload.count ? "count=" + payload.count : "from=" + encodeURIComponent(toApiDate(payload.from)) + "&to=" + encodeURIComponent(toApiDate(payload.to));
            var url = `${getTenantId()}/${this.apiNode}/weekly-standup?` + query;
            let { data } = await service.get(url);
            if (data) {
                this.SetWeeklyStandup(data.clients ?? []);
            }
            return data;
        }
        async SetWeeklyStandup(weeklyStandup) {
            this.weeklyStandup = weeklyStandup;
        }
        // Updates
        async AddUpdate(update) {
            var url = `${getTenantId()}/${this.apiNode}` + "/update";
            let { data } = await service.post(url, update);
            if (data) {
                return data;
            }
        }
        async EditUpdate(update) {
            var url = `${getTenantId()}/${this.apiNode}` + "/edit-update";
            let { data } = await service.post(url, update);
            if (data) {
                return data;
            }
        }
        async DeleteUpdate(updateId) {
            var url = `${getTenantId()}/${this.apiNode}/` + updateId + "/update";
            let { data } = await service.delete(url);
            if (data) {
                return data;
            }
        }
        async AddActionItem(actionItem) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}` + "/action-item";
            let { data } = await service.post(url, actionItem);
            if (data) {
                return data;
            }
        }
        async EditActionItem(actionItem) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}` + "/edit-action-item";
            let { data } = await service.post(url, actionItem);
            if (data) {
                return data;
            }
        }
        async DeleteActionItem(actionItemId) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}/` + actionItemId + "/action-item";
            let { data } = await service.delete(url);
            if (data) {
                return data;
            }
        }
        async CompleteActionItem(actionItemId) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}/` + actionItemId + "/complete-action-item";
            let { data } = await service.post(url);
            if (data) {
                return data;
            }
        }
        async ReopenActionItem(actionItemId) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}/` + actionItemId + "/reopen-action-item";
            let { data } = await service.post(url);
            if (data) {
                return data;
            }
        }
        async CloseActionItem(actionItemId) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}/` + actionItemId + "/close-action-item";
            let { data } = await service.post(url);
            if (data) {
                return data;
            }
        }
        // Action Items grid - every client, one status tab per request (same shape as ClientModule.GetClients)
        async GetActionItems(searchData) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}` + "/action-items";
            let { data } = await service.post(url, searchData);
            if (data) {
                this.SetActionItemGrid(plainToClass(ActionItemModel, data.actionItems ?? []));
                this.SetActionItemTotalCount(data.totalCount ?? 0);
                this.SetActionItemFilterOptions({ clients: data.clients ?? [], teams: data.teams ?? [], assignees: data.assignees ?? [], creators: data.creators ?? [] });
            }
            return data;
        }
        async SetActionItemGrid(actionItems) {
            this.actionItemGrid = actionItems;
        }
        async SetActionItemTotalCount(totalCount) {
            this.actionItemTotalCount = totalCount;
        }
        async SetActionItemFilterOptions(filterOptions) {
            this.actionItemFilterOptions = filterOptions;
        }
        // Eligible assignees (the same for every client) - used when the Action Item modal lets the user pick the client
        async GetEligibleAssignees() {
            var url = `${getTenantId()}/${this.actionItemsApiNode}/eligible-assignees`;
            let { data } = await service.get(url);
            if (data) {
                this.SetEligibleAssignees(data.eligibleAssignees ?? []);
            }
            return data;
        }
        async SetEligibleAssignees(eligibleAssignees) {
            this.eligibleAssignees = eligibleAssignees;
        }
        // The update an action item came from - the Action Items "View Update" popover
        async GetUpdateSummary(updateId) {
            var url = `${getTenantId()}/${this.actionItemsApiNode}/` + updateId + "/update-summary";
            let { data } = await service.get(url);
            if (data) {
                this.SetUpdateSummary(data.update ? plainToClass(UpdatesTimelineEntryModel, data.update) : null);
            }
            return data;
        }
        async SetUpdateSummary(updateSummary) {
            this.updateSummary = updateSummary;
        }
        // Attachments
        async AddAttachmentLink(payload) {
            var url = `${getTenantId()}/${this.apiNode}/` +
                payload.updateId +
                "/attachment-link?url=" +
                encodeURIComponent(payload.url) +
                "&label=" +
                encodeURIComponent(payload.label ?? "");
            let { data } = await service.post(url);
            if (data) {
                return data;
            }
        }
        async UploadAttachmentFile(payload) {
            var url = `${getTenantId()}/${this.apiNode}/` + payload.updateId + "/attachment-file";
            const formData = new FormData();
            formData.append("file", payload.file);
            let { data } = await service.post(url, formData);
            if (data) {
                return data;
            }
        }
        async DeleteAttachment(attachmentId) {
            var url = `${getTenantId()}/${this.apiNode}/` + attachmentId + "/attachment";
            let { data } = await service.delete(url);
            if (data) {
                return data;
            }
        }
        // Client picker options for the Updates filter bar
        async SetClientOptions(clientOptions) {
            this.clientOptions = clientOptions;
        }
        // Called when the Updates / Action Items / Weekly Standup pages are created and destroyed so no data outlives a visit
        ResetState() {
            this.weeklyStandup = [];
            this.timeline = [];
            this.tabCounts = { all: 0, client: 0, development: 0, actionItems: 0 };
            this.eligibleAssignees = [];
            this.teams = [];
            this.clientOptions = [];
            this.interactionTypes = [];
            this.actionItemGrid = [];
            this.actionItemTotalCount = 0;
            this.actionItemFilterOptions = emptyActionItemFilterOptions();
            this.updateSummary = null;
        }
    };
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", typeof (_a = typeof Promise !== "undefined" && Promise) === "function" ? _a : Object)
    ], ClientUpdatesModule.prototype, "GetUpdatesPageData", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetInteractionTypes", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_b = typeof Promise !== "undefined" && Promise) === "function" ? _b : Object)
    ], ClientUpdatesModule.prototype, "GetUpdatesTimeline", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_d = typeof Promise !== "undefined" && Promise) === "function" ? _d : Object)
    ], ClientUpdatesModule.prototype, "GetUpdatesTeams", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "ClearTimeline", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetTeams", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetTimeline", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetTabCounts", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_e = typeof Promise !== "undefined" && Promise) === "function" ? _e : Object)
    ], ClientUpdatesModule.prototype, "GetWeeklyStandup", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetWeeklyStandup", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [typeof (_f = typeof IClientUpdateDTO !== "undefined" && IClientUpdateDTO) === "function" ? _f : Object]),
        __metadata("design:returntype", typeof (_g = typeof Promise !== "undefined" && Promise) === "function" ? _g : Object)
    ], ClientUpdatesModule.prototype, "AddUpdate", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [typeof (_h = typeof IClientUpdateDTO !== "undefined" && IClientUpdateDTO) === "function" ? _h : Object]),
        __metadata("design:returntype", typeof (_j = typeof Promise !== "undefined" && Promise) === "function" ? _j : Object)
    ], ClientUpdatesModule.prototype, "EditUpdate", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_l = typeof Promise !== "undefined" && Promise) === "function" ? _l : Object)
    ], ClientUpdatesModule.prototype, "DeleteUpdate", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [typeof (_m = typeof IActionItemDTO !== "undefined" && IActionItemDTO) === "function" ? _m : Object]),
        __metadata("design:returntype", typeof (_o = typeof Promise !== "undefined" && Promise) === "function" ? _o : Object)
    ], ClientUpdatesModule.prototype, "AddActionItem", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [typeof (_p = typeof IActionItemDTO !== "undefined" && IActionItemDTO) === "function" ? _p : Object]),
        __metadata("design:returntype", typeof (_q = typeof Promise !== "undefined" && Promise) === "function" ? _q : Object)
    ], ClientUpdatesModule.prototype, "EditActionItem", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_s = typeof Promise !== "undefined" && Promise) === "function" ? _s : Object)
    ], ClientUpdatesModule.prototype, "DeleteActionItem", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_u = typeof Promise !== "undefined" && Promise) === "function" ? _u : Object)
    ], ClientUpdatesModule.prototype, "CompleteActionItem", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_w = typeof Promise !== "undefined" && Promise) === "function" ? _w : Object)
    ], ClientUpdatesModule.prototype, "ReopenActionItem", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_y = typeof Promise !== "undefined" && Promise) === "function" ? _y : Object)
    ], ClientUpdatesModule.prototype, "CloseActionItem", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [typeof (_z = typeof IActionItemsSearchDataDTO !== "undefined" && IActionItemsSearchDataDTO) === "function" ? _z : Object]),
        __metadata("design:returntype", typeof (_0 = typeof Promise !== "undefined" && Promise) === "function" ? _0 : Object)
    ], ClientUpdatesModule.prototype, "GetActionItems", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetActionItemGrid", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetActionItemTotalCount", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetActionItemFilterOptions", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", typeof (_1 = typeof Promise !== "undefined" && Promise) === "function" ? _1 : Object)
    ], ClientUpdatesModule.prototype, "GetEligibleAssignees", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetEligibleAssignees", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_3 = typeof Promise !== "undefined" && Promise) === "function" ? _3 : Object)
    ], ClientUpdatesModule.prototype, "GetUpdateSummary", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [typeof (_4 = typeof IUpdatesTimelineEntryDTO !== "undefined" && IUpdatesTimelineEntryDTO) === "function" ? _4 : Object]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetUpdateSummary", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_5 = typeof Promise !== "undefined" && Promise) === "function" ? _5 : Object)
    ], ClientUpdatesModule.prototype, "AddAttachmentLink", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_6 = typeof Promise !== "undefined" && Promise) === "function" ? _6 : Object)
    ], ClientUpdatesModule.prototype, "UploadAttachmentFile", null);
    __decorate([
        Action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", typeof (_8 = typeof Promise !== "undefined" && Promise) === "function" ? _8 : Object)
    ], ClientUpdatesModule.prototype, "DeleteAttachment", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", Promise)
    ], ClientUpdatesModule.prototype, "SetClientOptions", null);
    __decorate([
        Mutation,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], ClientUpdatesModule.prototype, "ResetState", null);
    ClientUpdatesModule = __decorate([
        Module({ dynamic: true, store: store, name: "clientUpdates", namespaced: true })
    ], ClientUpdatesModule);
    return ClientUpdatesModule;
})();
export default getModule(ClientUpdatesModule);
